
import { PDFDocument, rgb, grayscale, degrees } from 'pdf-lib';

import fontkit from '@pdf-lib/fontkit';

import { capitalize } from '../utils.js';

import { fetchUint8Array, drawText, savePDF } from './utils.js';

import openSansRegular from '../fonts/open-sans-regular.ttf';

import openSansBold from '../fonts/open-sans-bold.ttf';

import template from './activity-5-poster-2.pdf';

import redPushPinPath from '../images/activity-5-red-push-pin.png';

import greenPushPinPath from '../images/activity-5-green-push-pin.png';

import yellowPushPinPath from '../images/activity-5-yellow-push-pin.png';

import horizontalPlaceholderPath from '../images/activity-5-horizontal-placeholder.png';

import squarePlaceholderPath from '../images/activity-5-square-placeholder.png';

import { posterStyles } from '../stores/activity-5.js';

const style = posterStyles.find( ( { id } ) => id === 2 );

export async function exportPDF( firstName, lastName ) {

  const sansFontBytes = await fetchUint8Array( openSansRegular );
  
  const sansBoldFontBytes = await fetchUint8Array( openSansBold );

  const templateBytes = await fetchUint8Array( template );

  const redPushPinBytes = await fetch( redPushPinPath ).then( data => data.arrayBuffer() );

  const greenPushPinBytes = await fetch( greenPushPinPath ).then( data => data.arrayBuffer() );

  const yellowPushPinBytes = await fetch( yellowPushPinPath ).then( data => data.arrayBuffer() );

  const topImage1Bytes = await fetch( style.topImage1Path ?? squarePlaceholderPath ).then( data => data.arrayBuffer() );

  const topImage2Bytes = await fetch( style.topImage2Path ?? horizontalPlaceholderPath ).then( data => data.arrayBuffer() );

  const bottomImageBytes = await fetch( style.bottomImagePath ?? squarePlaceholderPath ).then( data => data.arrayBuffer() );

  const doc = await PDFDocument.load( templateBytes );
  
  doc.registerFontkit( fontkit );

  const sansFont = await doc.embedFont( sansFontBytes );
  
  const sansBoldFont = await doc.embedFont( sansBoldFontBytes );

  const redPushPinImage = await doc.embedPng( redPushPinBytes );

  const greenPushPinImage = await doc.embedPng( greenPushPinBytes );

  const yellowPushPinImage = await doc.embedPng( yellowPushPinBytes );

  const topImage1 = await doc.embedPng( topImage1Bytes );

  const topImage2 = await doc.embedPng( topImage2Bytes );

  const bottomImage = await doc.embedPng( bottomImageBytes );

  const page = doc.getPages()[ 0 ];

  const topImage1Scaled = topImage1.scaleToFit( 250, 250 );

  page.drawRectangle( {

    x: 60 - 10,
    
    y: page.getHeight() - topImage1Scaled.height - 100 - 10,

    width: topImage1Scaled.width + 20,
    
    height: topImage1Scaled.height + 20, 

    rotate: degrees( 5 ),
    
    color: rgb( 1, 1, 1 ),

    borderWidth: 2,

    borderColor: grayscale( 0.5 ),

    borderOpacity: 0.3

  } );

  page.drawImage( topImage1, {
    
    x: 60,
    
    y: page.getHeight() - topImage1Scaled.height - 100,
    
    width: topImage1Scaled.width,
    
    height: topImage1Scaled.height,
    
    rotate: degrees( 5 )
  
  } );

  const greenPushPinImageScaled = greenPushPinImage.scale( 0.5 );

  page.drawImage( greenPushPinImage, {
    
    x: 135,
    
    y: page.getHeight() - 100,
    
    width: greenPushPinImageScaled.width,
    
    height: greenPushPinImageScaled.height
  
  } );

  drawText( {
      
    doc, page, x: 50, y: 370, align: 'top left',
    
    font: sansBoldFont, size: 9, maxWidth: 145,
    
    text: ( style.topImage1Caption || 'No caption.' ).replace( /\n/g, ' ' )
  
  } );

  const topImage2Scaled = topImage2.scaleToFit( 300, 600 );

  page.drawRectangle( {

    x: 260 - 8,
    
    y: page.getHeight() - topImage2Scaled.height - 100 - 8,

    width: topImage2Scaled.width + 16,
    
    height: topImage2Scaled.height + 16, 

    rotate: degrees( -5 ),
    
    color: rgb( 1, 1, 1 ),

    borderWidth: 2,

    borderColor: grayscale( 0.5 ),

    borderOpacity: 0.3

  } );

  page.drawImage( topImage2, {
    
    x: 260,
    
    y: page.getHeight() - topImage2Scaled.height - 100,
    
    width: topImage2Scaled.width,
    
    height: topImage2Scaled.height,
    
    rotate: degrees( -5 )
  
  } );

  const yellowPushPinImageScaled = yellowPushPinImage.scale( 0.5 );

  page.drawImage( yellowPushPinImage, {
    
    x: 415,
    
    y: page.getHeight() - 130,
    
    width: yellowPushPinImageScaled.width,
    
    height: yellowPushPinImageScaled.height
  
  } );

  drawText( {
      
    doc, page, x: 430, y: 323, align: 'top left',
    
    font: sansBoldFont, size: 9, maxWidth: 145,
    
    text: ( style.topImage2Caption || 'No caption.' ).replace( /\n/g, ' ' )
  
  } );

  const bottomImageScaled = bottomImage.scaleToFit( 130, 130 );

  page.drawRectangle( {

    x: 70 - 10,
    
    y: page.getHeight() - bottomImageScaled.height - 460 - 10,

    width: bottomImageScaled.width + 20,
    
    height: bottomImageScaled.height + 20, 

    rotate: degrees( -5 ),
    
    color: rgb( 1, 1, 1 ),

    borderWidth: 2,

    borderColor: grayscale( 0.5 ),

    borderOpacity: 0.3

  } );

  page.drawImage( bottomImage, {
    
    x: 70,
    
    y: page.getHeight() - bottomImageScaled.height - 460,
    
    width: bottomImageScaled.width,
    
    height: bottomImageScaled.height,
    
    rotate: degrees( -5 )
  
  } );

  const redPushPinImageScaled = redPushPinImage.scale( 0.5 );

  page.drawImage( redPushPinImage, {
    
    x: 135,
    
    y: page.getHeight() - 475,
    
    width: redPushPinImageScaled.width,
    
    height: redPushPinImageScaled.height
  
  } );

  drawText( {
      
    doc, page, x: 60, y: 625, align: 'top left',
    
    font: sansBoldFont, size: 9, maxWidth: 145,
    
    text: ( style.bottomImageCaption || 'No caption.' ).replace( /\n/g, ' ' )
  
  } );

  let x = 255, y = 395, height = 0;

  ( { y, height } = 
  
    drawText( {
        
      doc, page, x, y, align: 'top left',
      
      font: sansBoldFont, size: 10, maxWidth: 300,
      
      text: ( style.subtitle || 'No subtitle.' ).replace( /\n/g, ' ' )
    
    } ) );

  ( { y, height } = 

    drawText( {
      
      doc, page, x, y: y + height + 8, align: 'top left',
      
      font: sansBoldFont, size: 22, lineHeight: 28, maxWidth: 300,
      
      text: ( style.title || 'No title.' ).replace( /\n/g, ' ' )
    
    } ) );

  drawText( {
    
    doc, page, x, y: y + height + 20, align: 'top left',
    
    font: sansFont, size: 10, maxWidth: 300,
    
    text: ( style.paragraph || 'No paragraph.' ).replace( /\n/g, ' ' )
  
  } );

  drawText( {
    
    doc, page, x: 50, y: 750, align: 'top left',
    
    font: sansBoldFont, size: 10,
    
    text: `Poster created by: ${ capitalize( firstName ) } ${ capitalize( lastName ) } (${ new Date().toLocaleString().split( ',' )[ 0 ] })`
  
  } );

  doc.setTitle( 'Activity Five - Poster 2' );
  
  doc.setSubject( 'Lewis Mountain' );
  
  doc.setCreator( 'Shenandoah National Park' );
    
  doc.setAuthor( 'National Park Service' );

  doc.setProducer( 'PDF-LIB (https://github.com/Hopding/pdf-lib)' );
  
  doc.setCreationDate( new Date() );
  
  doc.setModificationDate( new Date() );
  
  savePDF( `Lewis-Mountain-Activity-5-${ capitalize( firstName ) }-${ capitalize( lastName ) }`, doc );
  
}